<template>
  <div class="do-filters text-dark">
    <div class="do-filters__selected">
      <div class="do-filters__dropdown__container" v-on-clickaway="onClickOutside">
        <button class="do-bttn do-bttn__filter" @click="isShowDropdown = !isShowDropdown">
          <span class="icon-filter"></span>
          {{ i18n['substitutes'] }}
          <div class="do-filters__divider"></div>
          <span class="do-filters__selected-filters" v-if="allSelectedFiltersLength === 0">
            {{ i18n['select_filters'] }}
          </span>
          <template v-else>
            <span class="do-badge">
              {{ allSelectedFiltersLength }} {{ i18n['selected'] }}
            </span>
          </template>
        </button>
        <transition name="d-dropdown">
          <div class="do-filters__dropdown" v-if="isShowDropdown">
            <button @click="$emit('clearAll')" class="do-filters__button-transparent">{{ i18n['clear_all'] }}</button>
            <h4 class="do-filters__section-title" v-if="isSortable">
              <span class="icon-filter"></span>
              {{ i18n['filters'] }}
            </h4>
            <div class="do-filters__dropdown__item">
              <h5>{{ i18n['filter_by_criteria'] }}</h5>
              <button class="do-bttn__quick-filter"
                      :class="{'do-bttn__quick-filter--selected': getIsQuickFilterSelected(quickFilter)}"
                      v-for="quickFilter in quickFilters"
                      @click="$emit('toggleQuickFilter', quickFilter)"
                      :key="quickFilter.value">
                <span class="icon-checkmark-circle"></span>
                {{ i18n[quickFilter.titleKey] }}
              </button>
            </div>
            <div class="do-filters__dropdown__item">
              <h5>{{ i18n['filter_by_group'] }}</h5>
              <DoFiltersFilter
                :title="i18n['groups']"
                :tags="tags"
                :selectedTags="selectedTags"
                @select="$emit('select', $event)"
                @clear="$emit('clear')">
              </DoFiltersFilter>
              <div class="do-filters__presence" v-if="isPresenceOption && selectedTags.length > 0">
                <span class="do-filters__presence__label">{{ i18n['only_with_work_hours'] }}</span>
                <button class="do-bttn__quick-filter do-bttn__quick-filter--small"
                        :class="{'do-bttn__quick-filter--selected': groupsRequiringPresence.includes(tag)}"
                        v-for="tag in selectedTags"
                        :key="tag"
                        @click="$emit('togglePresenceGroup', tag)">
                  <span class="icon-checkmark-circle"></span>
                  {{ tag }}
                </button>
              </div>
            </div>
            <div class="do-filters__dropdown__item">
              <h5>{{ i18n['filter_by_blocks'] }}</h5>
              <DoFiltersFilter
                :title="i18n['blocks']"
                :tags="eventTitleTags"
                :selectedTags="selectedEventTitles"
                @select="$emit('selectEventTitle', $event)"
                @clear="$emit('clearEventTitles')">
              </DoFiltersFilter>
            </div>
            <div class="do-filters__sort" v-if="isSortable">
              <div class="do-filters__sort__header">
                <h4 class="do-filters__section-title">
                  <span class="icon-shuffle2"></span>
                  {{ i18n['sorting'] }}
                </h4>
                <div class="do-filters__sort__toggle">
                  {{ i18n['sort_by_filters'] }}
                  <AppToggle :propOn="isSortActive" @changed="onToggleSort"></AppToggle>
                </div>
              </div>
              <p class="do-filters__sort__hint">
                {{ sortHint }}
              </p>
              <draggable :value="sortItems" :disabled="!isSortActive" @input="onSortInput">
                <transition-group type="transition"
                                  name="do-filters-sort"
                                  tag="div"
                                  class="do-filters__sort__list"
                                  :class="{'do-filters__sort__list--inactive': !isSortActive}">
                  <div v-for="(item, idx) in sortItems"
                       :key="item.key"
                       class="do-filters__sort__item">
                    <div class="do-filters__sort__item__number">{{ idx + 1 }}</div>
                    <p>{{ getSortItemTitle(item) }}</p>
                    <span class="do-filters__sort__item__type">{{ i18n['type_' + item.type] }}</span>
                    <span class="icon-menu do-filters__sort__item__handle"></span>
                  </div>
                </transition-group>
              </draggable>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
const I18N = {
  da: {
    substitutes: 'Vikarer',
    select_filters: 'Vælg filtre',
    selected: 'valgt',
    clear_all: 'Ryd alle',
    filter_by_criteria: 'Vis alle der opfylder ét eller flere af følgende kriterier',
    filter_by_group: 'Eller er med i en eller flere af disse grupper',
    filter_by_blocks: 'Eller har en eller flere af disse brikker',
    groups: 'Grupper',
    blocks: 'Brikker',
    sub_events_available: 'Har rådighedstimer',
    sub_events_two_in_some_class: 'Er to eller flere samtidig i en klasse',
    sub_events_canceled: 'Har aflyste timer',
    filters: 'Filtre',
    sorting: 'Sortering',
    sorting_hint: 'Træk for at ændre rækkefølgen. Vikarer der matcher det øverste vises først.',
    sorting_empty: 'Slå et eller flere filtre til ovenfor for at sortere efter dem.',
    sorting_inactive: 'Slå sortering til for at vise vikarerne i rækkefølgen herunder.',
    sort_by_filters: 'Sortér efter filtre',
    only_with_work_hours: 'Kun med arbejdstid:',
    type_quick: 'Kriterie',
    type_group: 'Gruppe',
    type_block: 'Brik'
  },
  en: {
    substitutes: 'Substitutes',
    select_filters: 'Select filters',
    selected: 'selected',
    clear_all: 'Clear all',
    filter_by_criteria: 'Show all that meet one or more of the following criteria',
    filter_by_group: 'Or belong to one or more of these groups',
    filter_by_blocks: 'Or has one or more of these blocks',
    groups: 'Groups',
    blocks: 'Blocks',
    sub_events_available: 'Has available hours',
    sub_events_two_in_some_class: 'Two or more are in a class at the same time',
    sub_events_canceled: 'Has canceled hours',
    filters: 'Filters',
    sorting: 'Sorting',
    sorting_hint: 'Drag to change the order. Substitutes matching the top item are shown first.',
    sorting_empty: 'Turn on one or more filters above to sort by them.',
    sorting_inactive: 'Turn on sorting to show substitutes in the order below.',
    sort_by_filters: 'Sort by filters',
    only_with_work_hours: 'Only with work hours:',
    type_quick: 'Criterion',
    type_group: 'Group',
    type_block: 'Block'
  },
  nb: {
    substitutes: 'Vikarer',
    select_filters: 'Velg filtre',
    selected: 'valgt',
    clear_all: 'Fjern alle',
    filter_by_criteria: 'Vis alle som oppfyller ett eller flere av følgende kriterier',
    filter_by_group: 'Eller er med i en eller flere av disse gruppene',
    filter_by_blocks: 'Eller har en eller flere av disse brikkene',
    groups: 'Grupper',
    blocks: 'Brikker',
    sub_events_available: 'Har tilgjengelige timer',
    sub_events_two_in_some_class: 'To eller flere er samtidig i en klasse',
    sub_events_canceled: 'Har avlyste timer',
    filters: 'Filtre',
    sorting: 'Sortering',
    sorting_hint: 'Dra for å endre rekkefølgen. Vikarer som matcher det øverste vises først.',
    sorting_empty: 'Slå på ett eller flere filtre over for å sortere etter dem.',
    sorting_inactive: 'Slå på sortering for å vise vikarene i rekkefølgen nedenfor.',
    sort_by_filters: 'Sorter etter filtre',
    only_with_work_hours: 'Kun med arbeidstid:',
    type_quick: 'Kriterium',
    type_group: 'Gruppe',
    type_block: 'Brikke'
  }
}

import DDropdown from '../common/DDropdown.vue'
import DoFiltersFilter from './DoFiltersFilter.vue'
import draggable from 'vuedraggable'
import AppToggle from '../common/AppToggle.vue'
import {mixin as clickaway} from 'vue-clickaway'
export default {
  mixins: [clickaway],
  components: {DoFiltersFilter, DDropdown, draggable, AppToggle},
  props: {
    tags: {
      type: Array,
      default: () => []
    },
    selectedTags: {
      type: Array,
      default: () => [] // ['Vikarer', 'Team 1', etc]
    },
    selectedQuickFilters: {
      type: Array,
      default: () => []
    },
    eventTitleTags: {
      type: Array,
      default: () => []
    },
    selectedEventTitles: {
      type: Array,
      default: () => []
    },
    theSelectedSorting: {
      type: String,
      default: 'name'
    },
    isSortable: {
      type: Boolean,
      default: false
    },
    sortItems: {
      type: Array,
      default: () => [] // [{key: 'quick:sub_events_canceled', type: 'quick', value: 'sub_events_canceled'}]
    },
    isSortActive: {
      type: Boolean,
      default: false
    },
    isPresenceOption: {
      type: Boolean,
      default: false
    },
    groupsRequiringPresence: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isShowDropdown: false,
      quickFilters: [
        {value: 'sub_events_canceled', titleKey: 'sub_events_canceled'},
        {value: 'sub_events_available', titleKey: 'sub_events_available'},
        {value: 'sub_events_two_in_some_class', titleKey: 'sub_events_two_in_some_class'}
      ]
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale] || I18N['en']
    },
    allSelectedFiltersLength () {
      const quickFilters = Object.keys(this.selectedQuickFilters)
      return quickFilters.length + this.selectedTags.length + this.selectedEventTitles.length
    },
    sortHint () {
      if (this.sortItems.length === 0) return this.i18n['sorting_empty']
      return this.isSortActive ? this.i18n['sorting_hint'] : this.i18n['sorting_inactive']
    }
  },
  methods: {
    getIsQuickFilterSelected (quickFilter) {
      return this.selectedQuickFilters.find(selectedQuickFilter => selectedQuickFilter === quickFilter.value)
    },
    onClickOutside () {
      this.isShowDropdown = false
    },
    getSortItemTitle (item) {
      return item.type === 'quick' ? this.i18n[item.value] : item.value
    },
    onSortInput (items) {
      this.$emit('sortItems', items.map(item => item.key))
    },
    onToggleSort (isOn) {
      if (isOn !== this.isSortActive) {
        this.$emit('toggleSort', isOn)
      }
    }
  }
}
</script>

<style>
@import "../../assets/css/variables.css";

.do-bttn.do-bttn__filter {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  gap: 0.75rem;
  border: 1px dashed #e4e4e7;
  box-shadow: 0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(0, 0, 0, .05);
}

.do-bttn.do-bttn__filter:hover {
  background-color: #f4f4f5;
}

.do-bttn.do-bttn__filter > .icon-filter {
  font-size: 0.85rem;
}

.do-filters__divider {
  background: #e4e4e7;
  height: 65%;
  min-height: 2px;
  width: 1px;
}

.do-badge {
  background: #f4f4f5;
  border-radius: 4px;
  display: inline-block;
  font-size: 11px;
  font-weight: 400;
  padding: .15rem .35rem;
}

.do-filters__dropdown {
  width: 650px;
}

.do-bttn__quick-filter {
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #6c7a87;
  border: 1px solid #e4e4e7;
  opacity: 0.8;
  margin-right: 1rem;
}

.do-bttn__quick-filter > span {
  opacity: 0;
  width: 0;
}

.do-bttn__quick-filter.do-bttn__quick-filter--selected {
  background-color: aliceblue;
  opacity: 1;
  color: var(--primary);
  border-color: var(--primary);
}

.do-bttn__quick-filter.do-bttn__quick-filter--selected > span {
  opacity: 1;
  width: auto;
  margin-right: 0.75rem;
}

.do-filters__dropdown__item {
  margin-bottom: 1rem;
}

.do-filters__presence {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.do-filters__presence__label {
  font-size: 0.8rem;
  color: #6c7a87;
}

.do-bttn__quick-filter.do-bttn__quick-filter--small {
  margin-right: 0;
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
}

.do-filters__dropdown__item:last-child {
  margin-bottom: 0;
}

.sort-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.sort-item:not(.sort-item--selected) > span {
  opacity: 0;
}

.sort-item.sort-item--selected {
  color: var(--primary);
}

.do-filters__dropdown__item .d-dropdown-container {
  display: inline-block;
}

.do-filters__dropdown__container {
  position: relative;
}

.do-filters__dropdown {
  position: absolute;
  z-index: 19990;
  top: calc(100% + 2rem);
  right: 0;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, .24);
  height: auto;
  outline: none;
  text-align: left;
  padding: 0.5rem;
  min-width: 100px;
  margin-top: -10px !important;
}

.do-filters__section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c7a87;
}

.do-filters__section-title > span {
  font-size: 0.8rem;
}

.do-filters__sort {
  margin: 1rem -0.5rem -0.5rem;
  padding: 0.75rem 0.5rem 0.5rem;
  border-top: 1px solid #e4e4e7;
  background-color: #f8f9fb;
  border-radius: 0 0 6px 6px;
}

.do-filters__sort__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.do-filters__sort__header > .do-filters__section-title {
  margin: 0;
}

.do-filters__sort__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.8rem;
  font-weight: 400;
  color: #6c7a87;
}

.do-filters__sort__list--inactive {
  opacity: 0.5;
}

.do-filters__sort__list--inactive > .do-filters__sort__item {
  cursor: default;
}

.do-filters__sort__list--inactive > .do-filters__sort__item:hover {
  border-color: #e4e4e7;
}

.do-filters__sort__hint {
  margin: -0.25rem 0 0.5rem;
  font-size: 0.8rem;
  color: #6c7a87;
}

.do-filters__sort__list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.do-filters__sort__item {
  display: flex;
  align-items: stretch;
  background-color: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 4px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, .05);
  cursor: move;
  user-select: none;
  transform-origin: top center;
}

.do-filters__sort__item:hover {
  border-color: var(--primary);
}

.do-filters__sort__item__number {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  background-color: #f4f4f5;
  border-radius: 4px 0 0 4px;
  font-size: 0.8rem;
  color: #6c7a87;
}

.do-filters__sort__item > p {
  flex: 1;
  margin: 0;
  padding: 0.45rem 0.75rem;
}

.do-filters__sort__item__type {
  align-self: center;
  margin-right: 0.5rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background-color: #f4f4f5;
  font-size: 11px;
  color: #6c7a87;
}

.do-filters__sort__item__handle {
  align-self: center;
  padding: 0 0.75rem 0 0.25rem;
  color: #a1a1aa;
}

.do-filters-sort-move {
  transition: transform 0.3s;
}

.do-filters-sort-enter-active,
.do-filters-sort-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.do-filters-sort-enter,
.do-filters-sort-leave-to {
  opacity: 0;
  transform: scale(0.6);
}
</style>



// WEBPACK FOOTER //
// DoFilters.vue?18c91f41